import { BCP47, type Locale } from "@/i18n";

/**
 * Locale-aware formatting.
 *
 * `Intl` rather than hand-written formats: a German reader expects 1.234,50 €
 * and 29.08.2026, and getting that wrong in an invoice table is the kind of
 * detail that decides whether a tool feels local or translated.
 */

export function formatDate(locale: Locale, iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Intl.DateTimeFormat(BCP47[locale], {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(ms);
}

export function formatDateTime(locale: Locale, iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Intl.DateTimeFormat(BCP47[locale], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(ms);
}

/** Cents, because money in floating point is a bug waiting for an invoice. */
export function formatMoney(locale: Locale, cents: number, currency: string): string {
  return new Intl.NumberFormat(BCP47[locale], {
    style: "currency",
    currency,
  }).format(cents / 100);
}

export function formatNumber(locale: Locale, value: number): string {
  return new Intl.NumberFormat(BCP47[locale]).format(value);
}

/** "3 of 10" style ratios appear all over the seat and quota displays. */
export function formatPercent(locale: Locale, fraction: number): string {
  return new Intl.NumberFormat(BCP47[locale], {
    style: "percent",
    maximumFractionDigits: 0,
  }).format(fraction);
}
